import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  inject,
  signal,
  NgZone,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, takeUntil, finalize, expand, map, reduce, EMPTY } from 'rxjs';
import { OrderDetailsModalComponent } from '../components/order-details-modal/order-details-modal.component';
import { AddOrderModalComponent } from '../components/add-order-modal/add-order-modal.component';
import { CancelOrderModalComponent } from '../components/cancel-order-modal/cancel-order-modal.component';
import { OrderCancellationSuccessModalComponent } from '../components/order-cancellation-success-modal/order-cancellation-success-modal.component';
import { OrdersService } from './services/orders.service';
import { NotificationService } from '../services/notification.service';
import { RoleService } from '../services/role.service';
import { CancellationService } from '../services/cancellation.service';
import { OrderUtils } from './utils/order.utils';
import { RealtimeEventsService } from '../services/realtime-events.service';
import { bindPaymentSseEvents } from './utils/payment-realtime-sse.util';
import { bindOrderProcessedSseEvent } from './utils/order-processed-realtime-sse.util';
import {
  handlePaymentFailedFromContext,
  handlePaymentSuccessFromContext,
  PaymentEventContext,
} from './utils/orders-payment-events.util';
import { AuthService } from '../services/auth.service';
import { Order, OrderStatus, OrderStats, OrderFilters } from './models/order.models';
import {
  ORDER_SHIMMER_COLUMNS,
  createDefaultStaffOrderFilters,
  STAFF_ORDER_FILTER_CATEGORIES,
  ADMIN_ORDER_STATS,
  syncPackageFilterCategoryOptions,
  syncDiscountAppliedFilterCategoryOptions,
  DISCOUNT_APPLIED_FILTER_ALL,
  PaymentModeApiFilterItem,
} from './constants/orders.constants';
import {
  cloneOrderFilters,
  convertCategoriesToOrderFilters as convertSharedCategoriesToOrderFilters,
  isOptionAppliedFromOrderFilters,
  buildPaymentModeFiltersFromOrderFilters,
  buildWashTypeFiltersFromOrderFilters,
  countActiveOrderFilters,
  syncOrderPaymentModeFilterCategories,
  loadOrderPaymentModesForFilter,
  loadOrderTerminalPaymentFilterAvailability,
  applyOrderTerminalPaymentFilterVisibility,
  applyPostLoadOrderSort,
} from './utils/orders-page-shared.utils';
import { OrderListTableBase } from './order-list-page.base';
import { PackageService } from '../services/package.service';
import { TerminalSettingsService } from '../services/terminal-settings.service';
import { Package } from '../shared/types';
import { DiscountService } from '../discount/services/discount.service';
import { Discount } from '../discount/models/discount.models';
import { PaginationBarComponent } from '../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../shared/components/empty-result/empty-result.component';
import { LoadingTableShimmerComponent } from '../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import { StatsCardComponent } from '../shared/components/stats-card/stats-card.component';
import { SearchbarComponent } from '../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../shared/components/action-button/action-button.component';
import {
  FilterModalComponent,
  FilterCategory,
  FilterOption,
  FilterModalCustomRangeConfig,
} from '../shared/components/filter-modal/filter-modal.component';
import {
  SimpleDropdownComponent,
  DropdownOption,
} from '../shared/components/simple-dropdown/simple-dropdown.component';
import {
  DateRangePickerComponent,
  DateRange,
  PeriodOption,
} from '../shared/components/date-range-picker/date-range-picker.component';
import {
  subscribeToOrdersLprPlateUpdates,
  ordersLprPlateUpdateMarkForCheck,
} from './utils/orders-lpr-plate-update.util';
import { LoggerService } from '../services/logger.service';
import { LocationService } from '../services/location.service';
import { EmployeeService } from '../services/employee.service';
import { ApiService } from '../services/api.service';
import {
  ALL_LANES_FILTER_OPTION,
  loadLaneFilterOptions,
  shouldShowLaneFilter,
} from '../shared/utils/lane-filter.util';
import { getUserDataLocationId } from '../shared/utils/auth-headers.util';
import { OrdersListTableComponent } from './components/orders-list-table/orders-list-table.component';
import { OrdersActionsDropdownComponent } from './components/orders-actions-dropdown/orders-actions-dropdown.component';
import {
  CancelRefundDone,
  CancelRefundPanelComponent,
} from '../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import { CancelRefundIntent } from '../shared/components/cancel-refund-panel/cancel-refund-panel.logic';
import { CancelRefundService } from '../services/cancel-refund.service';

@Component({
  selector: 'app-staff-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OrderDetailsModalComponent,
    AddOrderModalComponent,
    CancelOrderModalComponent,
    OrderCancellationSuccessModalComponent,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    TitleBarComponent,
    StatsCardComponent,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
    SimpleDropdownComponent,
    DateRangePickerComponent,
    OrdersListTableComponent,
    OrdersActionsDropdownComponent,
    CancelRefundPanelComponent,
  ],
  templateUrl: './staff-orders.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./staff-orders.component.scss'],
})
export class StaffOrdersComponent extends OrderListTableBase implements OnInit, OnDestroy {
  @ViewChild(AddOrderModalComponent) addOrderModalComponent!: AddOrderModalComponent;
  @ViewChild(OrderDetailsModalComponent) orderDetailsModalComponent!: OrderDetailsModalComponent;
  searchTerm = '';
  itemsPerPage = 10;
  currentPage = 1;
  totalItems = 0;
  loading = false;

  // Lane filter (backend `lane_id`; empty = all lanes)
  laneFilterOptions: DropdownOption[] = [ALL_LANES_FILTER_OPTION];
  selectedLaneFilterId = '';
  isLaneDropdownOpen = false;

  // Hide the lane filter when there is at most one selectable lane.
  get showLaneFilter(): boolean {
    return shouldShowLaneFilter(this.laneFilterOptions);
  }

  // Date range picker properties
  selectedPeriod = 'Today';
  isDateRangePickerOpen = false;
  customDateRange: DateRange = { startDate: null, endDate: null };
  filterModalDateRange: DateRange = { startDate: null, endDate: null };
  filterModalRangeConfig: FilterModalCustomRangeConfig = this.buildFilterModalRangeConfig();

  // Period options for staff orders dropdown
  staffOrdersPeriodOptions: PeriodOption[] = [
    { label: 'Today', value: 'Today' },
    { label: 'This Week (Sun-Sat)', value: 'This Week' },
    { label: 'This Month', value: 'This Month' },
    { label: 'This Year', value: 'This Year' },
    { label: 'All Time', value: 'All Time' },
    { label: 'Select Date Range', value: 'Custom' },
  ];

  orderStats: OrderStats[] = ADMIN_ORDER_STATS.map(stat => ({ ...stat, value: '0' }));
  orderStatuses: OrderStatus[] = [];
  orders: Order[] = [];

  showFilterModal = false;

  // Filter categories for shared filter modal (includes Payment Mode multi-select)
  readonly filterCategories = signal<FilterCategory[]>(
    STAFF_ORDER_FILTER_CATEGORIES.map(category => ({
      ...category,
      options: category.options.map(option => ({ ...option })),
    }))
  );

  // Shimmer loading configuration
  shimmerColumns = ORDER_SHIMMER_COLUMNS;

  appliedFilters: OrderFilters = createDefaultStaffOrderFilters();

  // Current filters for synchronization between quick filters and main filter
  currentFilters: OrderFilters = createDefaultStaffOrderFilters();

  // Dropdown functionality
  openDropdownId: string | null = null;
  selectedOrderForDropdown: Order | null = null;

  // Order details modal
  showOrderDetailsModal = false;
  selectedOrder: Order | null = null;

  // Add order modal
  showAddOrderModal = false;

  // Cancel order modal
  showCancelOrderModal = false;
  orderToCancel: Order | null = null;
  /** CWS-1779: the cancel & refund panel and the row it was opened for. */
  showCancelRefundPanel = false;
  cancelRefundOrder: Order | null = null;
  cancelRefundIntent: CancelRefundIntent = 'cancel';
  /** Cancellation ids whose refund retry is in flight (CWS-1534 review: one retry per row). */
  private readonly retryingRefunds = new Set<string>();

  // Cancellation success modal
  showCancellationSuccessModal = false;
  cancellationRefundAmount = '';
  cancellationWasScheduled = false;

  // Sorting functionality
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | 'none' = 'none';

  // Wash type filter functionality

  readonly ordersService = inject(OrdersService);
  private readonly locationService = inject(LocationService);
  private readonly apiService = inject(ApiService);
  private readonly messageService = inject(NotificationService);
  private readonly roleService = inject(RoleService);
  private readonly cancellationService = inject(CancellationService);
  private readonly cancelRefundService = inject(CancelRefundService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly notificationService = inject(NotificationService);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly realtime = inject(RealtimeEventsService);
  private readonly logger = inject(LoggerService);
  private readonly packageService = inject(PackageService);
  private readonly discountService = inject(DiscountService);
  private readonly employeeService = inject(EmployeeService);
  private readonly terminalSettingsService = inject(TerminalSettingsService);

  /** Cached car-wash packages for the Package filter (cascade by Car Wash Type). */
  private filterPackages: Package[] = [];
  /** Cached discount definitions for the Discount Applied filter (normal + custom). */
  private filterDiscounts: Discount[] = [];
  /** Cached payment modes from GET `user/payment-modes` for the Payment Mode filter. */
  private filterPaymentModes: PaymentModeApiFilterItem[] = [];
  /** Show Terminal Payment filter only when the staff location has linked readers. */
  private showTerminalPaymentFilter = false;

  // Payment realtime via SSE (`GET /api/v1/events/stream`)
  private paymentSseSub: Subscription | null = null;

  // Background order-processing refresh via SSE (location + lane scoped)
  private orderProcessedSseSub: Subscription | null = null;
  private loadOrdersSub: Subscription | null = null;

  // Track processed payments to prevent duplicate toasts
  private processedPayments = new Set<string>();
  private destroy$ = new Subject<void>();
  private queryParamTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  // All features available to admin users

  ngOnInit(): void {
    this.initializeData();
    this.loadOrderStats(); // Load stats once on initialization
    this.loadFilterPackages();
    this.loadFilterDiscounts();
    this.loadFilterPaymentModes();
    this.loadTerminalPaymentFilterAvailability(this.getScopeLocationId());
    this.initializePaymentRealtime();
    this.ensureOrderProcessedRealtime();
    this.locationService
      .getLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ensureOrderProcessedRealtime();
        this.loadTerminalPaymentFilterAvailability(this.getScopeLocationId());
      });
    this.employeeService
      .getCurrentEmployeeProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.ensureOrderProcessedRealtime());

    // Subscribe to refresh events from OrdersService
    this.ordersService.refreshOrders$.pipe(takeUntil(this.destroy$)).subscribe((options: any) => {
      console.log('🔄 Refreshing orders table and stats...');
      if (options?.resetPage) {
        this.currentPage = 1;
      }
      this.loadOrders();
      this.loadOrderStats();
    });

    // Keep Discount Applied options fresh after create/update/delete on the Discount screen.
    this.discountService.discountsCacheCleared$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadFilterDiscounts());

    this.checkQueryParams();
    subscribeToOrdersLprPlateUpdates(
      this.notificationService,
      this.destroy$,
      {
        getOrders: () => this.orders,
        setOrders: orders => {
          this.orders = orders;
        },
        markForCheck: ordersLprPlateUpdateMarkForCheck(this.cdr),
        onOrderPatched: order => {
          if (this.selectedOrder?.id === order.id) {
            this.selectedOrder = order;
          }
        },
      },
      this.ngZone
    );
  }

  /** Load staff car-wash packages once for the Package filter options. */
  private loadFilterPackages(): void {
    this.packageService
      .getCarWashPackagesForStaff()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: packages => {
          this.filterPackages = Array.isArray(packages) ? packages : [];
          this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
        },
        error: () => {
          this.filterPackages = [];
          this.refreshPackageFilterOptions([]);
        },
      });
  }

  /**
   * Load all discount definitions (Summary + Custom) for the Discount Applied filter.
   * Pages through `package/discounts` (API max 100/page) with no `type` filter.
   */
  private loadFilterDiscounts(): void {
    const pageSize = 100;
    const fetchPage = (page: number) =>
      this.discountService.getDiscounts({
        page,
        limit: pageSize,
        sort_by: 'name',
        order_by: 'asc',
      });

    fetchPage(1)
      .pipe(
        expand(result => {
          const totalPages = result.pagination?.total_pages ?? 1;
          const currentPage = result.pagination?.page ?? 1;
          if (currentPage >= totalPages) {
            return EMPTY;
          }
          return fetchPage(currentPage + 1);
        }),
        map(result => result.rows ?? []),
        reduce((acc: Discount[], rows) => acc.concat(rows), [] as Discount[]),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: rows => {
          this.filterDiscounts = rows;
          this.refreshDiscountAppliedFilterOptions(
            this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
          );
        },
        error: () => {
          this.filterDiscounts = [];
          this.refreshDiscountAppliedFilterOptions(
            this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
          );
        },
      });
  }

  /** Load dynamic payment modes once for the Payment Mode filter options. */
  private loadFilterPaymentModes(): void {
    loadOrderPaymentModesForFilter(
      () => this.ordersService.getPaymentModesForFilter(),
      this.destroy$,
      modes => {
        this.filterPaymentModes = modes;
        this.refreshPaymentModeFilterOptions();
      }
    );
  }

  private refreshPackageFilterOptions(selectedPackageIds?: string[]): void {
    this.filterCategories.update(categories =>
      syncPackageFilterCategoryOptions(categories, this.filterPackages, selectedPackageIds)
    );
  }

  /** Rebuild Discount Applied options from cached discounts + current selection. */
  private refreshDiscountAppliedFilterOptions(selectedValue?: string): void {
    this.filterCategories.update(categories =>
      syncDiscountAppliedFilterCategoryOptions(
        categories,
        this.filterDiscounts,
        selectedValue ?? this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
      )
    );
  }

  private refreshPaymentModeFilterOptions(): void {
    this.filterCategories.update(categories =>
      syncOrderPaymentModeFilterCategories(
        categories,
        this.filterPaymentModes,
        this.appliedFilters.paymentMode,
        this.showTerminalPaymentFilter
      )
    );
  }

  private loadTerminalPaymentFilterAvailability(locationId: string): void {
    loadOrderTerminalPaymentFilterAvailability(
      id => this.terminalSettingsService.listReaders(id),
      locationId,
      this.destroy$,
      visible => this.setTerminalPaymentFilterVisible(visible)
    );
  }

  private setTerminalPaymentFilterVisible(visible: boolean): void {
    const hadTerminalFilter = !!this.appliedFilters.paymentMode?.terminal;
    const next = applyOrderTerminalPaymentFilterVisibility(
      visible,
      this.appliedFilters,
      this.currentFilters
    );
    this.showTerminalPaymentFilter = next.showTerminalPaymentFilter;
    this.appliedFilters = next.appliedFilters;
    if (next.currentFilters) {
      this.currentFilters = next.currentFilters;
    }
    this.refreshPaymentModeFilterOptions();
    if (hadTerminalFilter && !visible) {
      this.loadOrders();
    }
  }

  private checkQueryParams(): void {
    const orderId = new URLSearchParams(window.location.search).get('orderId');
    if (!orderId) return;

    this.queryParamTimeoutIds.push(
      setTimeout(() => {
        this.openOrderDetailsById(orderId);
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
      }, 100)
    );
  }

  private openOrderDetailsById(orderId: string): void {
    this.ordersService
      .getOrderById(orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (order: Order) => {
          this.selectedOrder = order;
          this.showOrderDetailsModal = true;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load order details. Please try again.',
          });
        },
      });
  }

  private formatDateForAPI(date: Date): string {
    // Format date in local timezone to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private initializeData(): void {
    this.orderStatuses = this.ordersService.getOrderStatuses();
    this.loadLaneFilterOptionsForShop();
    this.loadOrders();
  }

  private loadLaneFilterOptionsForShop(): void {
    const locationId = this.locationService.getShopLocation()?.id || getUserDataLocationId() || '';
    loadLaneFilterOptions(this.apiService, locationId).subscribe({
      next: options => {
        this.laneFilterOptions = options;
        if (
          this.selectedLaneFilterId &&
          !options.some(option => option.value === this.selectedLaneFilterId)
        ) {
          this.selectedLaneFilterId = '';
        }
      },
      error: () => {
        this.laneFilterOptions = [ALL_LANES_FILTER_OPTION];
      },
    });
  }

  toggleLaneDropdown(): void {
    this.isLaneDropdownOpen = !this.isLaneDropdownOpen;
  }

  closeLaneDropdown(): void {
    this.isLaneDropdownOpen = false;
  }

  selectLaneFilter(laneId: string): void {
    this.selectedLaneFilterId = laneId || '';
    this.isLaneDropdownOpen = false;
    this.currentPage = 1;
    this.loadOrders();
  }

  loadOrders(): void {
    this.loadOrdersSub?.unsubscribe();
    this.loading = true;

    // Convert main filter to API format

    const statusFilters = {
      completed: this.appliedFilters.status.completed,
      pending: this.appliedFilters.status.pending,
      cancelled: this.appliedFilters.status.canceled,
      scheduled: this.appliedFilters.status.scheduled,
      inProgress: this.appliedFilters.status.inProgress,
    };

    const washTypeFilters = buildWashTypeFiltersFromOrderFilters(this.appliedFilters);

    // Map period to API format - use the period from filters if set, otherwise use the selectedPeriod
    const periodToUse = this.appliedFilters.period || this.selectedPeriod;

    // Prepare period parameter with custom date range if applicable (similar to analytics module)
    let periodParam = periodToUse;
    if (
      periodToUse === 'Custom' &&
      this.customDateRange.startDate &&
      this.customDateRange.endDate
    ) {
      const startDate = this.formatDateForAPI(this.customDateRange.startDate);
      const endDate = this.formatDateForAPI(this.customDateRange.endDate);
      periodParam = `Custom:${startDate}:${endDate}`;
    }

    const periodFilter = this.ordersService.mapPeriodToApiFormat(periodParam);

    // Empty source filters object (not used anymore, but kept for API compatibility)
    const sourceFilters = {
      wayCom: false,
      scanAndGo: false,
      wayApp: false,
      website: false,
    };

    const paymentModeFilters = buildPaymentModeFiltersFromOrderFilters(this.appliedFilters);

    const request$ = this.ordersService.loadOrders(
      this.currentPage,
      this.itemsPerPage,
      this.searchTerm,
      '', // Legacy filterValue - not used
      washTypeFilters,
      statusFilters,
      sourceFilters,
      '', // Staff orders don't filter by location - they see only their assigned location
      periodFilter, // Pass the period filter in API format
      false,
      this.selectedLaneFilterId || undefined,
      paymentModeFilters,
      this.appliedFilters.packageIds ?? [],
      this.appliedFilters.discountApplied
    );

    this.loadOrdersSub = request$.subscribe({
      next: response => {
        let orders = response.orders;
        this.totalItems = response.totalItems;

        orders = applyPostLoadOrderSort(
          orders,
          this.sortColumn,
          this.sortDirection,
          this.ordersService
        );

        this.orders = orders;

        // Stats are loaded once on initialization, no need to reload on every order update
        this.loading = false;
      },
      error: error => {
        console.error('Error loading orders:', error);
        this.loading = false;
      },
    });
  }

  private loadOrderStats(): void {
    console.log('📊 Loading order statistics for staff...');
    this.ordersService.getOrderStatistics().subscribe({
      next: stats => {
        console.log('📊 Order statistics loaded:', stats);
        if (this.orderStats.length === stats.length) {
          stats.forEach((newStat, i) => {
            this.orderStats[i].value = newStat.value;
          });
        } else {
          this.orderStats = stats;
        }
      },
      error: error => {
        console.error('❌ Error loading order statistics:', error);
        console.log('📊 Using hardcoded stats due to API error');
        // Keep default stats with "0" values on error instead of clearing them
        // This ensures stats cards remain visible even when API fails
      },
    });
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.currentPage = 1;
    this.loadOrders();
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadOrders();
  }

  onFilter(): void {
    this.setFilterModalRange(this.customDateRange);
    this.showFilterModal = true;
    // Initialize filter categories with current applied state when opening modal
    this.resetFilterCategoriesToAppliedState();
    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
    this.refreshDiscountAppliedFilterOptions(
      this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
    );
  }

  onFilterModalClose(): void {
    this.showFilterModal = false;
  }

  private getDefaultFilters(): OrderFilters {
    return createDefaultStaffOrderFilters();
  }

  private deepCopyFilters(filters: OrderFilters): OrderFilters {
    return cloneOrderFilters(filters);
  }

  onFilterModalApply(filters: OrderFilters): void {
    this.appliedFilters = this.deepCopyFilters(filters);
    if (this.appliedFilters.period === 'custom') {
      this.customDateRange = {
        startDate: this.filterModalDateRange.startDate,
        endDate: this.filterModalDateRange.endDate,
      };
      this.setFilterModalRange(this.customDateRange);
    } else {
      this.customDateRange = { startDate: null, endDate: null };
      this.setFilterModalRange(this.customDateRange);
    }
    this.currentPage = 1;
    this.showFilterModal = false;

    // Main filter - call API to fetch filtered data
    this.loadOrders();
  }

  // New shared filter modal methods
  onFilterModalApplyShared(categories: FilterCategory[]): void {
    // Convert shared filter categories to OrderFilters format
    const filters = this.convertCategoriesToOrderFilters(categories);
    this.onFilterModalApply(filters);
  }

  onFilterModalDateRangeChange(range: DateRange): void {
    this.filterModalDateRange = {
      startDate: range.startDate ? new Date(range.startDate) : null,
      endDate: range.endDate ? new Date(range.endDate) : null,
    };
    this.updateFilterModalRangeConfig();
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
    // Reset checkboxes to match current applied filters when closing without applying
    this.resetFilterCategoriesToAppliedState();
    this.setFilterModalRange(this.customDateRange);
    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
    this.refreshDiscountAppliedFilterOptions(
      this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
    );
  }

  /** Cascade Package options when Car Wash Type checkboxes change. */
  onFilterOptionChange(event: { categoryId: string }): void {
    if (event.categoryId !== 'carWashType') {
      return;
    }
    this.refreshPackageFilterOptions();
  }

  private convertCategoriesToOrderFilters(categories: FilterCategory[]): OrderFilters {
    return convertSharedCategoriesToOrderFilters(categories, this.getDefaultFilters());
  }

  private setFilterModalRange(range: DateRange): void {
    this.filterModalDateRange = {
      startDate: range.startDate ? new Date(range.startDate) : null,
      endDate: range.endDate ? new Date(range.endDate) : null,
    };
    this.updateFilterModalRangeConfig();
  }

  private updateFilterModalRangeConfig(): void {
    this.filterModalRangeConfig = this.buildFilterModalRangeConfig();
  }

  private buildFilterModalRangeConfig(): FilterModalCustomRangeConfig {
    return {
      categoryId: 'period',
      triggerValue: 'Custom',
      startLabel: 'From',
      endLabel: 'To',
      startPlaceholder: 'Select start date',
      endPlaceholder: 'Select end date',
      startDate: this.filterModalDateRange.startDate,
      endDate: this.filterModalDateRange.endDate,
    };
  }

  private resetFilterCategoriesToAppliedState(): void {
    this.filterCategories.update((categories: FilterCategory[]) =>
      categories.map((category: FilterCategory) => ({
        ...category,
        options: category.options.map((option: FilterOption) => ({
          ...option,
          selected: this.isOptionApplied(category.id, option.value),
        })),
      }))
    );
    this.refreshPackageFilterOptions(this.appliedFilters.packageIds ?? []);
    this.refreshDiscountAppliedFilterOptions(
      this.appliedFilters.discountApplied ?? DISCOUNT_APPLIED_FILTER_ALL
    );
    this.refreshPaymentModeFilterOptions();
  }

  private isOptionApplied(categoryId: string, optionValue: string): boolean {
    return isOptionAppliedFromOrderFilters(this.appliedFilters, categoryId, optionValue);
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  get hasSearchOrFilters(): boolean {
    return (!!this.searchTerm && this.searchTerm.trim().length > 0) || this.hasActiveFilters;
  }

  get hasMainFiltersSelected(): boolean {
    return this.getSelectedFilterCount() > 0;
  }

  get hasFilterChanges(): boolean {
    return this.hasMainFiltersSelected || this.hasActiveFilters;
  }

  private getSelectedFilterCount(): number {
    return countActiveOrderFilters(this.appliedFilters);
  }

  getActiveFilterCount(): number {
    return countActiveOrderFilters(this.appliedFilters);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadOrders();
  }

  toggleDateRangePicker(): void {
    if (this.isDateRangePickerOpen) {
      this.closeDateRangePicker();
    } else {
      this.openDateRangePicker();
    }
  }

  openDateRangePicker(): void {
    this.isDateRangePickerOpen = true;
  }

  closeDateRangePicker(): void {
    this.isDateRangePickerOpen = false;
  }

  onDateRangePeriodChange(period: string): void {
    this.selectedPeriod = period;
  }

  onCustomDateRangeChange(dateRange: DateRange): void {
    this.customDateRange = dateRange;
  }

  onDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    this.selectedPeriod = data.period;
    if (data.customRange) {
      this.customDateRange = data.customRange;
    }
    this.closeDateRangePicker();
    this.currentPage = 1; // Reset to first page
    this.loadOrders(); // Reload orders with new period filter
  }

  // Dropdown methods
  toggleDropdown(event: Event, orderId: string): void {
    event.stopPropagation();

    if (this.openDropdownId === orderId) {
      this.closeDropdown();
    } else {
      this.openDropdown(event, orderId);
    }
  }

  private closeDropdown(): void {
    this.openDropdownId = null;
    this.selectedOrderForDropdown = null;
  }

  private openDropdown(event: Event, orderId: string): void {
    this.openDropdownId = orderId;
    this.selectedOrderForDropdown = this.orders.find(order => order.id === orderId) || null;

    // Position the dropdown correctly
    setTimeout(() => {
      const button = event.target as HTMLElement;
      const dropdown = document.querySelector('.actions-dropdown-menu') as HTMLElement;
      if (button && dropdown) {
        const rect = button.getBoundingClientRect();
        dropdown.style.top = `${rect.bottom + 5}px`;
        dropdown.style.left = `${rect.right - dropdown.offsetWidth}px`;
      }
    }, 0);
  }

  viewOrder(order: Order): void {
    this.closeDropdown();
    this.selectedOrder = order;
    this.showOrderDetailsModal = true;
  }

  /**
   * CWS-1779: where the server has decided the row (feature on at this shop),
   * Cancel opens the cancel & refund panel. Otherwise today's cancel dialog.
   */
  cancelOrder(order: Order): void {
    this.closeDropdown();
    if (order.cancellation?.available === true) {
      this.openCancelRefund(order, 'cancel');
      return;
    }
    this.orderToCancel = order;
    this.showCancelOrderModal = true;
  }

  /** CWS-1779: Refund on a completed item opens the cancel & refund panel (CWS-1778). */
  refundOrder(order: Order): void {
    this.closeDropdown();
    this.openCancelRefund(order, 'refund');
  }

  private openCancelRefund(order: Order, intent: CancelRefundIntent): void {
    this.cancelRefundOrder = order;
    this.cancelRefundIntent = intent;
    this.showCancelRefundPanel = true;
  }

  /** The order's own location; the page's scope when the row did not carry one. */
  get cancelRefundLocationId(): string {
    return this.cancelRefundOrder?.locationId || this.getScopeLocationId();
  }

  closeCancelRefundPanel(): void {
    this.showCancelRefundPanel = false;
    this.cancelRefundOrder = null;
  }

  /**
   * CWS-1947: the panel was closed without actioning anything.
   *
   * Reload the rows anyway. The row that offered Cancel is a snapshot from
   * whenever the list last loaded, and the usual reason an operator ends up
   * here is that it had gone stale -- the order auto-completed in the
   * background, the badge still read In Progress, and the panel had to tell
   * them the order can only be refunded now. Leaving the same stale row on
   * screen would send them straight back into it.
   */
  onCancelRefundClosed(): void {
    this.closeCancelRefundPanel();
    this.loadOrders();
  }

  /**
   * The panel executed a cancellation. As in the design, the modal closes and
   * the operator reads what happened in a toast; the row's status, flags and
   * the stat cards all moved, so the list reloads.
   */
  onCancelRefundDone(done?: CancelRefundDone): void {
    this.closeCancelRefundPanel();
    if (done) {
      const failed = done.result.status === 'refund_failed' || done.result.status === 'failed';
      this.messageService.add({
        severity: failed ? 'warn' : 'success',
        summary: failed
          ? 'Refund not sent'
          : done.result.cancellation?.action === 'refund'
            ? 'Refunded'
            : 'Cancelled',
        detail: done.summary,
      });
    }
    this.loadOrders();
    this.loadOrderStats();
  }

  /**
   * CWS-1779: the row's last cancellation still owes a refund. Retry it in
   * place; the cancellation itself is already done, so there is nothing to
   * ask the operator again.
   */
  retryRefund(order: Order): void {
    this.closeDropdown();
    const cancellationId = order.cancellation?.refund_failed?.cancellation_id;
    if (!cancellationId) return;
    // One retry per row at a time: the request takes a Stripe round trip, the row keeps its
    // badge until the list reloads, and a second click meanwhile must not reach the server.
    if (this.retryingRefunds.has(cancellationId)) {
      this.messageService.add({
        severity: 'info',
        summary: 'Retrying refund',
        detail: `The refund on ${order.orderId} is already being retried.`,
      });
      return;
    }
    this.retryingRefunds.add(cancellationId);
    this.cancelRefundService
      .retryRefund(cancellationId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.retryingRefunds.delete(cancellationId))
      )
      .subscribe({
        next: res => {
          if (res.status && res.data && res.data.status !== 'refund_failed') {
            this.messageService.add({
              severity: 'success',
              summary: 'Refund sent',
              detail: `The refund on ${order.orderId} has gone out.`,
            });
            this.onCancelRefundDone();
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Refund failed again',
            detail:
              res.message || `The refund on ${order.orderId} could not be sent. Try again later.`,
          });
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Refund failed again',
            detail: err?.error?.message || `The refund on ${order.orderId} could not be sent.`,
          });
        },
      });
  }

  onCancelOrderConfirm(data: { order: Order; reason: string; action?: string }): void {
    if (data.action === 'book-again') {
      // Staff cannot add orders - this action is not available
      return;
    } else {
      // Call the cancel order API
      this.cancellationService.cancelOrder(data.order.id, data.reason).subscribe({
        next: response => {
          if (response.status) {
            // Check if it was a scheduled order before updating status
            const wasScheduledOrder = data.order?.status?.value === 'scheduled';

            // Store the order price before updating status
            const orderPrice = data.order?.price || '';

            // Set properties for cancellation success modal
            this.cancellationWasScheduled = wasScheduledOrder;
            this.cancellationRefundAmount = orderPrice;
            this.showCancellationSuccessModal = true;

            this.loadOrders(); // Reload orders to get updated status
            // Refresh statistics to update cards
            this.loadOrderStats();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to cancel order. Please try again.',
            });
          }
        },
        error: error => {
          console.error('Error cancelling order:', error);
          console.error('Error details:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            error: error.error,
          });
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to cancel order. Please try again.',
          });
        },
      });
    }

    this.closeCancelOrderModal();
  }

  closeCancelOrderModal(): void {
    this.showCancelOrderModal = false;
    this.orderToCancel = null;
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (!target.closest('.actions')) {
      this.closeDropdown();
    }

    // Close period dropdown if clicking outside
    const dropdownWrapper = target.closest('.period-dropdown-wrapper');
    const dateRangePicker = target.closest('.dropdown-menu');
    const primeCalendar = target.closest('.p-datepicker') || target.closest('.p-calendar');
    const primeCalendarPanel = target.closest('.p-datepicker-panel');
    const primeCalendarHeader = target.closest('.p-datepicker-header');
    const primeCalendarMonth = target.closest('.p-datepicker-month');
    const primeCalendarYear = target.closest('.p-datepicker-year');
    const primeCalendarNav =
      target.closest('.p-datepicker-prev') || target.closest('.p-datepicker-next');
    const primeCalendarButton =
      target.closest('.p-datepicker-prev-icon') || target.closest('.p-datepicker-next-icon');

    if (
      !dropdownWrapper &&
      !dateRangePicker &&
      !primeCalendar &&
      !primeCalendarPanel &&
      !primeCalendarHeader &&
      !primeCalendarMonth &&
      !primeCalendarYear &&
      !primeCalendarNav &&
      !primeCalendarButton &&
      this.isDateRangePickerOpen
    ) {
      this.closeDateRangePicker();
    }

    if (!target.closest('.wash-type-header')) {
      /* intentionally empty */
    }
  }

  // Sorting functionality — inherited from OrderListTableBase

  // Generic filter methods
  private openGenericFilterModal<T>(
    event: Event,
    headerSelector: string,
    modalSelector: string,
    showModalProperty: keyof this,
    filtersProperty: keyof this,
    appliedFiltersProperty: keyof this
  ): void {
    (this as any)[filtersProperty] = { ...(this as any)[appliedFiltersProperty] };
    (this as any)[showModalProperty] = true;
    this.positionModal(event, headerSelector, modalSelector);
  }

  private closeGenericFilterModal<T>(
    showModalProperty: keyof this,
    filtersProperty: keyof this,
    appliedFiltersProperty: keyof this
  ): void {
    (this as any)[showModalProperty] = false;
    (this as any)[filtersProperty] = { ...(this as any)[appliedFiltersProperty] };
  }

  private clearGenericFilters<T>(filtersProperty: keyof this, getDefaultMethod: () => T): void {
    (this as any)[filtersProperty] = getDefaultMethod();
  }

  private applyGenericFilters<T>(
    filtersProperty: keyof this,
    appliedFiltersProperty: keyof this,
    showModalProperty: keyof this
  ): void {
    (this as any)[appliedFiltersProperty] = { ...(this as any)[filtersProperty] };
    (this as any)[showModalProperty] = false;
    this.currentPage = 1;
  }

  private hasSelectedFilters<T>(filtersProperty: keyof this): boolean {
    return Object.values((this as any)[filtersProperty]).some((filter: any) => filter);
  }

  private hasAppliedFilters<T>(appliedFiltersProperty: keyof this): boolean {
    return Object.values((this as any)[appliedFiltersProperty]).some((filter: any) => filter);
  }

  private positionModal(event: Event, headerSelector: string, modalSelector: string): void {
    setTimeout(() => {
      const header = (event.target as HTMLElement).closest(headerSelector) as HTMLElement;
      const modal = document.querySelector(modalSelector) as HTMLElement;

      if (header && modal) {
        const rect = header.getBoundingClientRect();
        const modalWidth = 240; // Fixed width from CSS

        // Find the filter icon within the header (works for all filter types)
        const filterIcon = header.querySelector('.filter-icon') as HTMLElement;
        let iconLeft = rect.left;

        if (filterIcon) {
          const iconRect = filterIcon.getBoundingClientRect();
          iconLeft = iconRect.left;
        }

        modal.style.top = `${rect.bottom + 5}px`;
        modal.style.left = `${iconLeft}px`;
      }
    }, 0);
  }

  // Order details modal methods
  openOrderDetails(order: Order): void {
    this.selectedOrder = order;
    this.showOrderDetailsModal = true;
  }

  closeOrderDetails(): void {
    this.showOrderDetailsModal = false;
    this.selectedOrder = null;
  }

  onOrderCancelled(_cancelledOrder: Order): void {
    this.loadOrders();
    this.closeOrderDetails();
  }

  onBookAgainFromOrderDetails(): void {
    // Open the add order modal
    this.showAddOrderModal = true;
  }

  onCancellationSuccessClose(): void {
    this.showCancellationSuccessModal = false;
  }

  onCancellationBookAgain(): void {
    // Close the cancellation success modal
    // Note: Staff cannot add orders, so this action may not be available
    this.showCancellationSuccessModal = false;
  }

  canCancelOrder(order: Order): boolean {
    const canBeCancelled = OrderUtils.canBeCancelled(order);
    if (!canBeCancelled) return false;

    if (this.roleService.isAdmin() || this.roleService.isSuperAdmin()) {
      return true;
    }

    if (this.roleService.isShopFloorEmployee()) {
      const currentUser = this.authService.user();
      if (currentUser?.id != null && order.userId != null) {
        return String(currentUser.id) === String(order.userId);
      }
      return false;
    }

    return true;
  }

  canShowActions(_order: Order): boolean {
    return true;
  }

  onAddOrder(): void {
    console.log('onAddOrder called - opening modal');
    this.showAddOrderModal = true;
  }

  get onAddOrderClick() {
    return () => this.onAddOrder();
  }

  onCloseAddOrder(): void {
    this.showAddOrderModal = false;
  }

  onOrderCreated(): void {
    // Refresh orders list when a new order is created
    this.loadOrders();
    // Refresh statistics to update cards
    this.loadOrderStats();
  }

  onViewOrderDetails(orderId: string): void {
    this.showAddOrderModal = false;
    this.loadOrders();
    this.loadOrderStats();
    this.openOrderDetailsById(orderId);
  }

  onContinueAddOrder(_payload: { tierId: string; optionId: string }): void {
    // For now, just close modal and keep placeholder for future steps
    this.showAddOrderModal = false;
  }

  ngOnDestroy(): void {
    this.queryParamTimeoutIds.forEach(id => clearTimeout(id));
    this.queryParamTimeoutIds = [];

    this.loadOrdersSub?.unsubscribe();
    this.loadOrdersSub = null;

    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up payment realtime
    this.disconnectPaymentRealtime();
    this.disconnectOrderProcessedRealtime();
  }

  /**
   * Background order-processing refresh via SSE (`GET /api/v1/events/stream`).
   * Staff pages are server-scoped to their assigned location, so we still filter
   * client-side by that location (multiplexed stream). Skip binding until a
   * concrete scope location is available so empty scope cannot match all events.
   */
  private ensureOrderProcessedRealtime(): void {
    const locationId = this.getScopeLocationId();
    if (this.orderProcessedSseSub || !locationId) {
      return;
    }
    this.orderProcessedSseSub = bindOrderProcessedSseEvent(this.realtime, this.ngZone, {
      getLocationId: () => this.getScopeLocationId(),
      getSelectedLaneId: () => this.selectedLaneFilterId,
      onOrderProcessed: data => {
        this.logger.trackOrder('SSE order processed — refreshing table and stats', {
          data,
          locationId: this.getScopeLocationId(),
          laneFilterId: this.selectedLaneFilterId || null,
        });
        this.loadOrders();
        this.loadOrderStats();
      },
    });
  }

  private disconnectOrderProcessedRealtime(): void {
    this.orderProcessedSseSub?.unsubscribe();
    this.orderProcessedSseSub = null;
  }

  /** Location this staff user is scoped to (for client-side SSE filtering). */
  private getScopeLocationId(): string {
    return this.locationService.getShopLocation()?.id || getUserDataLocationId() || '';
  }

  /**
   * Handle payment success event and refresh orders
   */
  private handlePaymentSuccess(eventName: string, data: any): void {
    handlePaymentSuccessFromContext(this.getPaymentEventContext(), eventName, data);
  }

  /**
   * Handle payment failure event
   */
  private handlePaymentFailed(eventName: string, data: any): void {
    handlePaymentFailedFromContext(this.getPaymentEventContext(), eventName, data);
  }

  private getPaymentEventContext(): PaymentEventContext {
    return {
      processedPayments: this.processedPayments,
      messageService: this.messageService,
      closePaymentLinkModal: () => this.addOrderModalComponent?.closePaymentLinkModal(),
      closeAddOrderModal: () => {
        this.showAddOrderModal = false;
      },
      emitGlobalPaymentSucceeded: orderId => this.ordersService.emitPaymentSucceeded(orderId),
      loadOrders: () => this.loadOrders(),
      showOrderDetailsModal: this.showOrderDetailsModal,
      orderDetailsModalComponent: this.orderDetailsModalComponent,
      selectedOrder: this.selectedOrder,
      logger: this.logger,
    };
  }

  /**
   * Payment realtime via SSE (`GET /api/v1/events/stream`).
   * Envelope `channel` is metadata only (order-{locationId}|order-{userId}); FE filters by order_id / list refresh.
   */
  private initializePaymentRealtime(): void {
    this.paymentSseSub = bindPaymentSseEvents(this.realtime, this.ngZone, {
      onSuccess: (eventName, data) => this.handlePaymentSuccess(eventName, data),
      onFailed: (eventName, data) => this.handlePaymentFailed(eventName, data),
      skipNamedInCatchAll: true,
    });
  }

  private disconnectPaymentRealtime(): void {
    this.paymentSseSub?.unsubscribe();
    this.paymentSseSub = null;
    this.processedPayments.clear();
  }
}
